// Employee/hooks/useEvents.tsx
import {useCallback, useContext, useEffect, useState} from "react";
import {AvailabilityInterval, Consultation, Event, EventAddData,} from "../types";
import {formatISO, isValid} from "date-fns";
import {toZonedTime} from "date-fns-tz";
import {AuthContext, AuthContextType} from "../../Auth/AuthContext";
import {axios} from "Auth/axiosConfig";
import {Message} from "Core/components/Message";
import {useProfile} from "../../User/ProfileContext";
import pprint from '../../../Utils/pprint';

export function useEvents(
    userType: string,
    canEdit: boolean,
    consultationsData: Consultation[],
    availabilityIntervalsData: AvailabilityInterval[],
    chosenTimeZone?: string
) {
    const {user, isAuthenticated} = useContext(AuthContext) as AuthContextType;
    const {selectedProfile} = useProfile();
    const [events, setEvents] = useState<Event[]>([]);
    const [editingEvent, setEditingEvent] = useState<Event | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [timeZone, setTimeZone] = useState(
        chosenTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
    );
    const [pendingEventAdd, setPendingEventAdd] = useState<EventAddData | null>(
        null
    );

    const changeTimeZone = useCallback((newTimeZone: string) => {
        setTimeZone(newTimeZone);
        pprint(`Timezone changed to: ${newTimeZone}`); // Optional: Log timezone change or trigger side effects
        Message.info(`Часовой пояс изменен на: ${newTimeZone}`); // Optional: Notify the user
    }, [setTimeZone]);

    useEffect(() => {
        if (!user) {
            // If the user is not authenticated or not loaded yet, don't create events
            return;
        }

        const getConsultationColor = (status: string) => {
            switch (status) {
                case "NEW":
                    return "#FFA726"; // Orange
                case "PAID":
                    return "#4CAF50"; // Green
                case "COMPLETED":
                    return "#9E9E9E"; // Grey
                case "AVAILABLE":
                    return "#2196F3"; // Blue
                default:
                    return "#2196F3"; // Blue (default)
            }
        };

        const convertToChosenTime = (dateString: string) => {
            const date = new Date(dateString);
            if (!isValid(date)) {
                console.error(`Invalid date string: ${dateString}`);
                return dateString;
            }
            // Use toZonedTime to convert to the specified time zone before formatting
            const zonedDate = toZonedTime(date, timeZone);
            return formatISO(zonedDate); // ISO format preserves the time zone info for use elsewhere
        };

        const consultationEvents: Event[] = consultationsData.map((consultation) => ({
            id: consultation.id,
            user: user.id,
            start: convertToChosenTime(consultation.date),
            end: convertToChosenTime(
                new Date(
                    new Date(consultation.date).getTime() +
                    (consultation.config.duration.value * 60000) // Accessing duration from config
                ).toISOString()
            ),
            status: consultation.status,
            title: `Consultation: ${consultation.clients[0]?.full_name}`,
            color: getConsultationColor(consultation.status),
            duration: consultation.config.duration, // Update to reflect the correct structure
            clients: consultation.clients,
            psychologists: consultation.psychologists,
            conference: consultation.conference,
        }));

        const availabilityEvents: Event[] = availabilityIntervalsData.map(
            (interval) => ({
                id: interval.id,
                user: user.id,
                start: convertToChosenTime(interval.start),
                end: convertToChosenTime(interval.end),
                status: "AVAILABLE",
                title: "Available",
                color: getConsultationColor("AVAILABLE"),
                duration: null,
            })
        );

        setEvents([...consultationEvents, ...availabilityEvents]);
    }, [timeZone, consultationsData, availabilityIntervalsData, user]);

    const handleEventEdit = useCallback(
        (event: Event | null) => {
            if (canEdit || (userType === "client" && event?.status === "AVAILABLE")) {
                setEditingEvent(event);
                setIsModalOpen(true);
            }
        },
        [canEdit, userType]
    );

    const handleEventAdd = useCallback(
        (eventData: EventAddData) => {
            if (userType === "client") {
                setPendingEventAdd(eventData);
                setEditingEvent(null);
                setIsModalOpen(true);
            } else {
                addEvent(eventData);
            }
        },
        [userType]
    );

    const addEvent = useCallback((eventData: EventAddData) => {
        const {originalEvent, proposedStart, proposedEnd} = eventData;

        if (!user) {
            return;
        }

        // Convert to the chosen timezone for consistency
        const zonedProposedStart = toZonedTime(proposedStart, timeZone);
        const zonedProposedEnd = toZonedTime(proposedEnd, timeZone);

        const consultationData = {
            start: zonedProposedStart.toISOString(),
            end: zonedProposedEnd.toISOString(),
            psychologist_id: user.id,
            duration_id: originalEvent.duration?.id,
        };

        const apiUrl = selectedProfile === "psychologist"
            ? "/api/v1/psychologist/consultations/"
            : "/api/v1/client/consultations/";

        axios.post(apiUrl, consultationData).then((response) => {
            // Process API response as before...
            const createdConsultation = response.data;

            const newEvent: Event = {
                id: createdConsultation.id,
                user: createdConsultation.psychologists[0].id,
                start: createdConsultation.date,
                end: new Date(
                    new Date(createdConsultation.date).getTime() +
                    createdConsultation.duration.value * 60000
                ).toISOString(),
                status: createdConsultation.status,
                title: `Consultation: ${createdConsultation.clients[0].full_name}`,
                color: "#FFA726",
                duration: createdConsultation.duration,
                clients: createdConsultation.clients,
                psychologists: createdConsultation.psychologists,
                conference: createdConsultation.conference,
            };

            // Update events state with the new event
            setEvents((prevEvents) => {
                const filteredEvents = prevEvents.filter((event) => event.id !== originalEvent.id);

                const beforeEvent: Event | null = new Date(originalEvent.start) < proposedStart
                    ? {...originalEvent, id: `before_${newEvent.id}`, end: zonedProposedStart.toISOString()}
                    : null;

                const afterEvent: Event | null = new Date(originalEvent.end) > proposedEnd
                    ? {...originalEvent, id: `after_${newEvent.id}`, start: zonedProposedEnd.toISOString()}
                    : null;

                return [
                    ...filteredEvents,
                    newEvent,
                    ...(beforeEvent ? [beforeEvent] : []),
                    ...(afterEvent ? [afterEvent] : []),
                ];
            });

            setPendingEventAdd(null);
            setIsModalOpen(false);
            setEditingEvent(null);
            Message.success("Консультация создана успешно.");
        }).catch((error) => {
            console.error("Error creating consultation:", error);
            Message.error("Не удалось создать консультацию.");
        });
    }, [user, selectedProfile, timeZone, setEvents, setPendingEventAdd, setIsModalOpen, setEditingEvent]);


    const handleEventSave = useCallback((updatedEvent: Event) => {
        const isAvailability = updatedEvent.status === "AVAILABLE";
        const endpoint = isAvailability
            ? `/api/v1/employee/availability/${updatedEvent.id}/`
            : `/api/v1/consultations/${updatedEvent.id}/`;

        const eventData = {
            start: updatedEvent.start,
            end: updatedEvent.end,
            ...(isAvailability ? {} : {status: updatedEvent.status}),
        };

        axios
            .put(endpoint, eventData)
            .then((response) => {
                setEvents((prevEvents) => {
                    const eventIndex = prevEvents.findIndex(
                        (e) => e.id === updatedEvent.id
                    );
                    if (eventIndex !== -1) {
                        const newEvents = [...prevEvents];
                        newEvents[eventIndex] = {
                            ...updatedEvent,
                            ...response.data,
                        };
                        return newEvents;
                    } else {
                        return [...prevEvents, {...updatedEvent, ...response.data}];
                    }
                });
                setIsModalOpen(false);
                setEditingEvent(null);
                Message.success("Событие успешно обновлено.");
            })
            .catch((error) => {
                console.error("Error updating event:", error);
                Message.error("Не удалось обновить событие.");
            });
    }, []);

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingEvent(null);
    };

    const onRemove = useCallback((eventToRemove: Event) => {
        if (eventToRemove.status === "AVAILABLE") {
            axios
                .delete(`/api/v1/employee/availability/${eventToRemove.id}/`)
                .then(() => {
                    setEvents((prevEvents) =>
                        prevEvents.filter((event) => event.id !== eventToRemove.id)
                    );
                    Message.success("Интервал доступности успешно удален.");
                })
                .catch((error) => {
                    console.error("Error deleting availability interval:", error);
                    Message.error("Не удалось удалить интервал доступности.");
                });
        } else {
            setEvents((prevEvents) =>
                prevEvents.filter((event) => event.id !== eventToRemove.id)
            );
        }
    }, []);

    const handleCreateAvailability = useCallback((start: Date, end: Date) => {
        const formattedStart = formatISO(start);
        const formattedEnd = formatISO(end);

        axios
            .post("/api/v1/employee/availability/", {
                start: formattedStart,
                end: formattedEnd,
            })
            .then((response) => {
                const newAvailabilityEvent: Event = {
                    id: response.data.id,
                    user: response.data.user,
                    start: response.data.start,
                    end: response.data.end,
                    title: "Available",
                    status: "AVAILABLE",
                    color: "#2196F3",
                    duration: null,
                };

                // Convert the new event's times to the current timezone
                const adjustedEvent = {
                    ...newAvailabilityEvent,
                    start: formatISO(toZonedTime(new Date(newAvailabilityEvent.start), timeZone)),
                    end: formatISO(toZonedTime(new Date(newAvailabilityEvent.end), timeZone))
                };

                setEvents((prevEvents) => [...prevEvents, adjustedEvent]);
                Message.success("Интервал доступности создан успешно.");
            })
            .catch((error) => {
                console.error("Error creating availability interval:", error);
                Message.error("Не удалось создать интервал доступности.");
            });
    }, [timeZone]);

    return {
        events,
        editingEvent,
        isModalOpen,
        handleEventEdit,
        handleEventAdd,
        handleEventSave,
        closeModal,
        setEvents,
        onRemove,
        pendingEventAdd,
        addEvent,
        setPendingEventAdd,
        handleCreateAvailability,
        changeTimeZone,
        setIsModalOpen
    };
}
